import setAppPaneUnderlayVisibility from 'owa-app-pane-underlay/lib/actions/setAppPaneUnderlayVisibility';
import { isCapabilityEnabled } from 'owa-capabilities';
import { newHeaderCapability } from 'owa-capabilities-definitions/lib/newHeaderCapability';
import { PinnablePane } from 'owa-outlook-service-option-store';
import { isSuiteHeaderRendered, setPendingAutoOpenFlexPaneId } from 'owa-suite-header-store';
import openFlexPaneHelper from 'owa-app-pane-group-utils/lib/helpers/openFlexPaneHelper';
import isFlexPaneShownHelper from 'owa-app-pane-group-utils/lib/helpers/isFlexPaneShownHelper';
import { isBizchatSidepaneEnabled } from 'owa-bizchat-shared/lib/utils/isBizchatSidepaneEnabled';
import { getPinnablePaneRegistration } from './getPinnablePaneRegistrations';
import { isPanePinningEnabled } from './isPanePinningEnabled';
import { isGlobalFeatureEnabled } from 'owa-feature-flags';
import { handleCopilotChatAutoOpen } from './handleCopilotChatAutoOpen';

/**
 * Set up app pane underlay and schedule an auto-open operation if pre-requisites are met
 */
export function tryAutoOpen(pinnablePane: PinnablePane): void {
    if (!isPanePinningEnabled() || pinnablePane === PinnablePane.None) {
        return;
    }

    const paneRegistration = getPinnablePaneRegistration(pinnablePane);
    const shouldPaneAutoOpen = paneRegistration?.shouldAutoOpen?.() ?? true;
    if (!paneRegistration || !shouldPaneAutoOpen) {
        return;
    }

    // Check if we're trying to open the new Copilot Chat Drawer, which replaces the Copilot Chat FlexPane.
    // For the time being, the Copilot Chat Drawer hooks into the FlexPane sticky open logic,
    // but we don't want to set the app pane underlay visibility, as the Drawer is not a FlexPane
    // and lives in a different part of the app layout.
    if (
        paneRegistration.flexPaneId === 'ChatCopilot' &&
        isGlobalFeatureEnabled('mon-copilot-bizchat-drawer')
    ) {
        // Need to check whether user is eligible to see BizChat at all.
        // Note this function returns three states:
        // - Undefined: Means the call has not finished, and we should put the Drawer into a pending state
        //              which means it'll retry auto open once the check finishes.
        // - True: BizChat enabled, should auto open.
        // - False: BizChat disabled, do not auto open.
        const isBizChatSidepaneEnabled = isBizchatSidepaneEnabled();
        if (isBizChatSidepaneEnabled === undefined) {
            // queue pending operation for owa-copilot-chat-drawer-store orchestrator
            pendingAutoOpenOperation = handleCopilotChatAutoOpen;
        } else if (isBizChatSidepaneEnabled) {
            paneRegistration?.handleAutoOpen?.();
        }
        return;
    }

    const isSuiteHeader = !isCapabilityEnabled(newHeaderCapability);
    if (isSuiteHeader) {
        // set up app pane underlay to pre-reserve space in the app layout, since auto-open will take place
        // otherwise layout thrashing will occur shortly after initial render due to pane auto-open pushing app content content to the side
        // NOTE: this is only a concern for large-screen scenarios, since pane is rendered as overlay on top of app content for small screens
        setAppPaneUnderlayVisibility(
            'suiteFlexPane',
            true /* isVisible */,
            true /* isShrinkable */,
            true /* isPlaceholder */
        );
    }

    const autoOpenOperation =
        paneRegistration.handleAutoOpen ??
        (() => openFlexPaneHelper(paneRegistration.flexPaneId, 'chatCopilot'));

    if (isSuiteHeaderRendered() && isSuiteHeader && !isFlexPaneShownHelper()) {
        autoOpenOperation();
    } else {
        if (isSuiteHeader) {
            setPendingAutoOpenFlexPaneId(paneRegistration.flexPaneId);
        }
        // queue pending operation for suite-header-rendered orchestrator
        // no need to check for flex pane collision in this scenario
        pendingAutoOpenOperation = autoOpenOperation;
    }
}

let pendingAutoOpenOperation: (() => void) | undefined = undefined;

export function getPendingAutoOpenOperation(): (() => void) | undefined {
    return pendingAutoOpenOperation;
}

export function clearPendingAutoOpenOperation(): void {
    pendingAutoOpenOperation = undefined;
    setPendingAutoOpenFlexPaneId(undefined);
}
